import { useMemo } from 'react';
import useTable from '@/hooks/useTable';
import type { GsBulletin } from '@/lib/types';
import { SPONSOR_ORDERS, REGION_BY_ID, packageById } from '@/pages/demo/revenue/components/revenueData';
import { EDITORIAL_QUEUE, ACTIVITY, regionName } from './adminData';
import { LEADS, isOpen, leadValueLabel, STAGE_TONE } from './sponsorData';
import { COVERAGE, regionsByOpenSlots } from './sponsorCoverage';
import { MULTILINGUAL_QUEUE, languageLabel } from '@/lib/multilingual';
import type { ActivityItem, EditorialItem } from './adminData';
import { sectionById } from './navConfig';
import type { SectionId } from './navConfig';
import StatusBadge from './StatusBadge';
import type { BadgeTone } from './StatusBadge';
import AdminStat from './AdminStat';

type QueueRow = { id: string; title: string; meta: string; badge: string; tone: BadgeTone };
type Queue = { id: string; title: string; icon: string; section: SectionId; action: string; rows: QueueRow[]; empty: string };

const TOURNAMENT_SUBMISSIONS: QueueRow[] = [
  { id: 'ts-1', title: 'Spring Charity Scramble', meta: 'Griffith Park · submitted by organizer', badge: 'Pending', tone: 'review' },
  { id: 'ts-2', title: 'Junior Skills Classic', meta: 'Newport Beach · submitted by organizer', badge: 'Pending', tone: 'review' },
  { id: 'ts-3', title: 'North County Open', meta: 'Carlsbad · submitted by organizer', badge: 'Pending', tone: 'review' },
];

const SPONSOR_INQUIRIES: QueueRow[] = LEADS.filter(isOpen)
  .slice(0, 4)
  .map((l) => ({
    id: l.id,
    title: l.company,
    meta: `${packageById(l.packageId ?? '')?.name ?? 'Custom quote'} · ${l.regionIds.map(regionName).join(', ')} · ${leadValueLabel(l)}`,
    badge: l.stage,
    tone: STAGE_TONE[l.stage],
  }));

function toneForStatus(status: string): BadgeTone {
  if (status === 'Needs review') return 'review';
  if (status === 'Incoming') return 'scheduled';
  if (status === 'Revisions requested') return 'alert';
  if (status === 'Published') return 'live';
  return 'draft';
}

function toneForTranslation(stage: string): BadgeTone {
  if (stage === 'Published') return 'live';
  if (stage === 'Approved') return 'done';
  if (stage === 'In review') return 'review';
  if (stage === 'Draft') return 'scheduled';
  return 'draft';
}

export default function CommandCenter({ onGo }: { onGo: (id: SectionId) => void }) {
  const { data: bulletins } = useTable<GsBulletin>('gs_bulletins');

  const pendingBulletins = useMemo(() => bulletins.filter((b) => (b.status || 'pending') === 'pending'), [bulletins]);

  const incoming = EDITORIAL_QUEUE.filter((e) => e.status === 'Incoming');
  const aiDrafts = EDITORIAL_QUEUE.filter((e) => e.status === 'AI-prepared' && e.aiPrepared);
  const approvals = EDITORIAL_QUEUE.filter((e) => e.status === 'Needs review');
  const renewals = SPONSOR_ORDERS.filter((o) => o.renewsInDays > 0 && o.renewsInDays <= 30).sort((a, b) => a.renewsInDays - b.renewsInDays);
  const openSlotMarkets = regionsByOpenSlots(COVERAGE).filter((r) => r.open > 0).slice(0, 4);

  const toRow = (e: EditorialItem): QueueRow => ({
    id: e.id,
    title: e.form.headline,
    meta: `${e.source} · ${e.regionIds.map(regionName).join(', ')}`,
    badge: e.status,
    tone: toneForStatus(e.status),
  });

  const queues: Queue[] = [
    { id: 'incoming', title: 'Incoming stories', icon: 'ri-inbox-archive-line', section: 'editorial', action: 'Review', rows: incoming.map(toRow), empty: 'No incoming stories.' },
    { id: 'ai', title: 'AI-prepared drafts', icon: 'ri-sparkling-line', section: 'editorial', action: 'Open', rows: aiDrafts.map(toRow), empty: 'No AI-prepared drafts.' },
    { id: 'approvals', title: 'Editorial approvals needed', icon: 'ri-checkbox-circle-line', section: 'editorial', action: 'Review', rows: approvals.map(toRow), empty: 'Nothing awaiting approval.' },
    { id: 'tournaments', title: 'Tournament submissions', icon: 'ri-trophy-line', section: 'tournaments', action: 'Review', rows: TOURNAMENT_SUBMISSIONS, empty: 'No submissions.' },
    {
      id: 'bulletins',
      title: 'Community bulletins',
      icon: 'ri-pushpin-2-line',
      section: 'bulletins',
      action: 'Moderate',
      rows: pendingBulletins.slice(0, 3).map((b) => ({ id: `b-${b.id}`, title: b.title, meta: `${b.tag}${b.meta ? ` · ${b.meta}` : ''}`, badge: 'Pending', tone: 'review' as BadgeTone })),
      empty: 'No notices awaiting moderation.',
    },
    {
      id: 'translations',
      title: 'Translations awaiting review',
      icon: 'ri-translate-2',
      section: 'multilingual',
      action: 'Review',
      rows: MULTILINGUAL_QUEUE.filter((m) => m.stage !== 'Not started').map((m) => ({
        id: m.id,
        title: m.headline,
        meta: `${languageLabel(m.originalLang)} → ${languageLabel(m.targetLang)} · ${regionName(m.regionId)}`,
        badge: m.stage,
        tone: toneForTranslation(m.stage),
      })),
      empty: 'No translations awaiting review.',
    },
    { id: 'inquiries', title: 'Sponsor inquiries', icon: 'ri-megaphone-line', section: 'sponsors', action: 'Open', rows: SPONSOR_INQUIRIES, empty: 'No new inquiries.' },
    {
      id: 'coverage',
      title: 'Open sponsorship slots',
      icon: 'ri-map-pin-2-line',
      section: 'revenue',
      action: 'View',
      rows: openSlotMarkets.map((r) => ({
        id: r.region.id,
        title: r.region.city,
        meta: `${r.open} slot${r.open === 1 ? '' : 's'} open · ${r.packages.filter((p) => p.open > 0).map((p) => p.name).join(', ')}`,
        badge: `${r.open} open`,
        tone: 'done' as BadgeTone,
      })),
      empty: 'Every market is at capacity.',
    },
    {
      id: 'renewals',
      title: 'Campaign renewals',
      icon: 'ri-refresh-line',
      section: 'revenue',
      action: 'View',
      rows: renewals.slice(0, 3).map((o) => ({ id: o.id, title: o.sponsor, meta: `${packageById(o.packageId)?.name} · ${REGION_BY_ID[o.regionId].city}`, badge: `${o.renewsInDays}d`, tone: 'alert' as BadgeTone })),
      empty: 'No renewals due in 30 days.',
    },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4 md:gap-5">
        <AdminStat label="Incoming stories" value={String(incoming.length)} note="Awaiting first review" icon="ri-inbox-archive-line" />
        <AdminStat label="Approvals needed" value={String(approvals.length)} note="Editorial decisions" icon="ri-checkbox-circle-line" />
        <AdminStat label="Bulletins pending" value={String(pendingBulletins.length)} note="Community moderation" icon="ri-pushpin-2-line" />
        <AdminStat label="Renewals due" value={String(renewals.length)} note="Within 30 days" icon="ri-refresh-line" />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
        {queues.map((q) => (
          <section key={q.id} className="bg-background-50 border border-background-200 rounded-lg overflow-hidden">
            <div className="px-4 md:px-5 py-3.5 border-b border-background-200 flex items-center justify-between gap-3">
              <div className="flex items-center gap-2.5 min-w-0">
                <span className="w-8 h-8 flex items-center justify-center rounded-md bg-primary-100 text-primary-600 shrink-0"><i className={q.icon}></i></span>
                <p className="font-heading font-bold text-base md:text-lg text-foreground-950 truncate">{q.title}</p>
                <span className="font-label font-bold text-[11px] text-foreground-500 bg-background-100 border border-background-200 rounded-full px-2 py-0.5 shrink-0">{q.rows.length}</span>
              </div>
              <button type="button" onClick={() => onGo(q.section)} className="font-label uppercase tracking-wide text-[11px] font-semibold text-primary-600 hover:text-primary-700 cursor-pointer whitespace-nowrap">
                {sectionById(q.section).label} →
              </button>
            </div>
            {q.rows.length === 0 ? (
              <p className="px-5 py-6 text-sm text-foreground-500">{q.empty}</p>
            ) : (
              <ul className="divide-y divide-background-200">
                {q.rows.map((r) => (
                  <li key={r.id} className="px-4 md:px-5 py-3 flex items-center gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-foreground-950 truncate">{r.title}</p>
                      <p className="text-xs text-foreground-500 truncate">{r.meta}</p>
                    </div>
                    <StatusBadge tone={r.tone}>{r.badge}</StatusBadge>
                    <button type="button" onClick={() => onGo(q.section)} className="font-label uppercase tracking-wide text-[11px] font-semibold text-foreground-700 hover:text-primary-600 cursor-pointer whitespace-nowrap">
                      {q.action}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>
        ))}
      </div>

      {/* Recent activity */}
      <section className="bg-background-50 border border-background-200 rounded-lg overflow-hidden">
        <div className="px-5 py-3.5 border-b border-background-200 flex items-center gap-2.5">
          <span className="w-8 h-8 flex items-center justify-center rounded-md bg-accent-100 text-accent-800"><i className="ri-history-line"></i></span>
          <p className="font-heading font-bold text-lg text-foreground-950">Recent activity</p>
        </div>
        <ul className="divide-y divide-background-200">
          {ACTIVITY.map((a: ActivityItem) => (
            <li key={a.id} className="px-4 md:px-5 py-3 flex items-center gap-3">
              <span className="w-9 h-9 flex items-center justify-center rounded-md bg-background-100 text-foreground-600 shrink-0"><i className={a.icon}></i></span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-foreground-950 truncate">{a.title}</p>
                <p className="text-xs text-foreground-500 truncate">{a.detail}</p>
              </div>
              <span className="font-label uppercase tracking-wide text-[10px] text-foreground-500 whitespace-nowrap hidden sm:block">{a.time}</span>
              <button type="button" onClick={() => onGo(a.section as SectionId)} className="font-label uppercase tracking-wide text-[11px] font-semibold text-foreground-700 hover:text-primary-600 cursor-pointer whitespace-nowrap">
                Open
              </button>
            </li>
          ))}
        </ul>
      </section>

      <p className="text-xs text-foreground-500 flex items-center gap-1.5">
        <span className="w-4 h-4 flex items-center justify-center"><i className="ri-information-line"></i></span>
        Sample operations data for the client walkthrough — no live figures are shown here.
      </p>
    </div>
  );
}